import type { CountryConfig } from './types.js';
import { kgPublic } from './kg.public.js';

/**
 * Shape validation for a Kyrgyz ИНН (идентификационный налоговый номер).
 *
 * A KG ИНН is 14 digits. For a person (it doubles as the ПИН) it is one type
 * digit (1 / 2) + the birth date DDMMYYYY + a 5-digit serial; for a legal
 * entity it is `0` + the registration date DDMMYYYY + a 5-digit serial.
 *
 * ## Why there is no check-digit verification here
 *
 * The State Tax Service (ГНС КР) publishes no check-digit algorithm for the
 * ИНН, and the last five digits are documented as a serial, not as a serial +
 * control digit. Guessing one is strictly worse than having none — the UZ
 * lesson (`uz.ts`): a wrong formula silently rejects real taxpayers, and the
 * failure reads as «the site says my ИНН is invalid» with nothing in the logs.
 *
 * Deliberately NOT validated either: the leading type digit and the embedded
 * date. Legal entities re-registered after a reorganisation, pre-1991 dates and
 * clerical variants all occur on genuine records, and the same rule over-rejected
 * on Kazakhstan (`kz.ts`, the ИИН/БИН date prefix). Shape, not existence:
 * the authoritative check is the ГНС registry lookup, server-side, if ever needed.
 *
 * ## What IS rejected
 *
 * Anything that is not 14 digits, all-identical runs (`00000000000000`) and
 * ascending / descending runs (`12345678901234`) — the placeholders that the UZ
 * base proved end up in `users.tin` and then fabricate awards.
 */
const TIN_ALL_SAME = /^(\d)\1{13}$/;

/** A run like `12345678901234` or `98765432109876`: every step +1 (or −1) mod 10. */
function isSequentialTin(tin: string): boolean {
  const step = (a: string, b: string) => (Number(b) - Number(a) + 10) % 10;
  const first = step(tin[0], tin[1]);
  if (first !== 1 && first !== 9) return false;
  for (let i = 1; i < tin.length - 1; i++) {
    if (step(tin[i], tin[i + 1]) !== first) return false;
  }
  return true;
}

export function isValidKgInn(tin: string): boolean {
  if (!/^\d{14}$/.test(tin)) return false;
  if (TIN_ALL_SAME.test(tin)) return false;
  if (isSequentialTin(tin)) return false;
  return true;
}

export const kgConfig: CountryConfig = {
  ...kgPublic,
  // Owner, 2026-11-04. The three state portals (goszakupki.okmot.kg — the new
  // JSON portal; zakupki.gov.kg/popp — the old JSF portal; zakupki.okmot.kg/popp —
  // state companies and municipalities) and the IFI
  // adapters. ЕЭП КР (mitwork.kg, 3 lots in its whole history at the 04.11 recon)
  // is NOT listed until the `mitwork` adapter reads its origin from scrape_config —
  // today it hard-codes eep.mitwork.kz and would harvest Kazakh lots.
  // Followed by the IFI
  // adapters made KG-ready by a scrape_config override alone (see KG_SOURCE_SEED).
  // The scheduler intersects registered adapters against this list, so a key here
  // must also be registered and have an enabled KG `tender_sources` row to harvest.
  sourceKeys: [
    'kg-goszakupki',
    'kg-zakupki-gov',
    'kg-zakupki-okmot',
    'worldbank',
    'isdb',
    'ebrd',
    'adb',
    'aiib',
    'undp',
    'ungm',
  ],
  // Direct contracts are named by the procurement METHOD (as on KZ): «метод
  // прямого заключения договора» on the old portals, «из одного источника» in
  // the new portal's vocabulary. A row with no method is not counted as a
  // competition — no measurement yet, so the KZ caution applies.
  competition: {
    nonCompetitiveSources: [],
    nonCompetitiveMethodMarkers: ['одного источника', 'прямого заключения', 'прямое заключение'],
    unknownMethodIsCompetitive: false,
  },
  companyId: {
    label: 'ИНН',
    validate: isValidKgInn,
  },
  // Stub like KZ: ОКГЗ codes arrive from the source per lot position; nothing is
  // inferred from a title. A blank code is «no code». The stored form is the
  // 8-digit code WITHOUT its check digit (`45210000`, as the KG adapters emit
  // it); a dashed `45210000-2` that slips through is folded to that form.
  // `vocabularyBucket: 'level2'` is a placeholder — neither existing bucket fits a
  // CPV-shaped code (`45210000` has no dot and no 6-digit class); the picker's
  // mined vocabulary sees no KG lot until a CPV bucket exists (lane notes).
  classifier: {
    label: 'ОКГЗ',
    vocabularyBucket: 'level2',
    resolve: (code, _title) => {
      const c = code?.trim();
      return c ? c.replace(/^(\d{8})-\d$/, '$1') : null;
    },
    classify: (_title) => null,
  },
  // An ОКГЗ code typed into the catalog search box — the stored 8-digit code, the
  // dashed form pasted from a portal (`45210000-2`, the check digit is dropped
  // before matching) or a 5–7 digit prefix (`45213`) — is answered as the `ikpu`
  // prefix filter, as on KZ (checklist #2; `catalog/code-query.ts`). Narrower than
  // `productCodePattern` on purpose, like KZ's: a bare 2–4 digit number typed into
  // the box is a year («2025»), a quantity or a price («5000»), not a code — read as
  // one it found nothing (no ОКГЗ division 20) or an unrelated prefix («5000» → the
  // 50… repair services). It stays a text query. A 2–4 digit class is still a code
  // through the catalogue's ОКГЗ filter (`?ikpu=4521`). A bare number of 6+ digits
  // that names a lot is still answered as that lot first (`lot-number-search.ts`).
  searchCodePattern: /^(?:\d{8}-\d|\d{5,8})$/,
  // The KZ rule (owner, 2026-11-04): 5 competitive awards in 36 months, no ACTIVE
  // test — no Kyrgyz register is wired to say ACTIVE.
  companyIndex: {
    minCompetitiveAwards: 5,
    windowMonths: 36,
    requireActiveStatus: false,
  },
  // Som. ⚠️ A FIRST GUESS, not measured — no KG price distribution exists yet.
  // Floor 100 som (~$1.1; symbolic 1–10 som sums sit below it on KZ-like data),
  // ceiling 100 млрд som (~$1.1 bn, far above any Kyrgyz contract seen on the
  // portals at recon). Re-measure on the first 12 months of awards, as KZ did.
  awardPlausibility: { floor: 100, ceiling: 100_000_000_000 },
  // The new portal publishes PRODUCT / SERVICE / WORK per lot; the old portals'
  // kind is derived from the ОКГЗ division (`kg-category.map.ts`). The generated
  // column's Uzbek rule («41–43 и 71 = работы») is wrong for CPV-shaped codes
  // (works are division 45), so the source kind must win.
  procurementKindFromSource: true,
  // ОКГЗ has no dots: a code matches by plain prefix, and a full 8-digit code
  // stands for its tree node (`45210000` → `4521…`) — a filter, a saved search or
  // a niche on `4521` finds `45210000` and `45213100` (checklist #1).
  productCodePrefix: 'flat',
  // Som. PROPOSALS, not owner decisions: budget floor = the award floor; the
  // bid-package «сложный ярус» at 20 млн som (~$230k, a staged construction
  // contract); the stuck-checkout split at 4 000 som — between Pro (2 990) and
  // Business (5 990) monthly, as KZ splits between its two plans.
  thresholds: {
    minPlausibleBudget: 100,
    complexLotBudget: 20_000_000,
    stuckCheckoutBand: 4_000,
  },
};
